import React from 'react';

import withAuth from './withAuth';

/**
 * withAuth HOCの使用例を示すコンポーネント
 */

// 基本的な使用例：認証が必要なコンポーネント
const CommentForm: React.FC = () => {
  return (
    <div className="p-4 border rounded-lg">
      <h3 className="text-lg font-semibold mb-2">コメントを投稿</h3>
      <textarea
        className="w-full p-2 border rounded"
        placeholder="コメントを入力してください..."
        rows={3}
      />
      <button className="mt-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
        投稿
      </button>
    </div>
  );
};

// 認証が必要なコメントフォーム
export const AuthenticatedCommentForm = withAuth(CommentForm);

// カスタムフォールバックを使用した例
const LoginPrompt: React.FC = () => (
  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
    <p className="text-yellow-800 mb-2">コメントを投稿するにはログインしてください</p>
    <button className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700">
      ログイン / 登録
    </button>
  </div>
);

export const CommentFormWithCustomFallback = withAuth(CommentForm, {
  fallback: LoginPrompt,
});

// 条件付きレンダリングの例（お気に入りボタン）
const FavoriteButton: React.FC<{ itemId: string }> = ({ itemId }) => {
  return (
    <button className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600">
      ☆ お気に入り
    </button>
  );
};

// 認証済みユーザーにのみ表示されるお気に入りボタン
export const ConditionalFavoriteButton = withAuth(FavoriteButton, {
  conditionalRender: true,
});

// 認証不要だが、認証状態によって表示が変わるコンポーネント
const UserGreeting: React.FC = () => {
  return (
    <div className="p-2">
      <p>ようこそ！ログインしています。</p>
    </div>
  );
};

const GuestGreeting: React.FC = () => {
  return (
    <div className="p-2">
      <p>ゲストとしてご利用中です。</p>
    </div>
  );
};

// 認証状態に応じて異なるコンポーネントを表示
export const AuthenticatedGreeting = withAuth(UserGreeting, {
  fallback: GuestGreeting,
});

// 使用例を表示するデモコンポーネント
const AuthGuardExample: React.FC = () => {
  return (
    <div className="space-y-6 p-6">
      <h2 className="text-2xl font-bold">withAuth HOC 使用例</h2>

      <div>
        <h3 className="text-lg font-semibold mb-2">1. 基本的な認証ガード</h3>
        <AuthenticatedCommentForm />
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">2. カスタムフォールバック</h3>
        <CommentFormWithCustomFallback />
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">3. 条件付きレンダリング</h3>
        <div className="flex items-center space-x-2">
          <span>恐竜の詳細</span>
          <ConditionalFavoriteButton itemId="trex" />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">4. 認証状態による表示切り替え</h3>
        <AuthenticatedGreeting />
      </div>
    </div>
  );
};

export default AuthGuardExample;
